"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import {
  defaultRegion,
  findNearestRegion,
  findRegionByCityName,
  knownRegions,
  type SiteRegion
} from "@/lib/location-data";
import { fixMaybeBrokenText } from "@/lib/text-client";

type RegionContextValue = {
  isReady: boolean;
  region: SiteRegion;
  setRegion: (region: SiteRegion) => void;
  setRegionByCityName: (city: string) => void;
};

const STORAGE_KEY = "bitstone_region_v1";
const RegionContext = createContext<RegionContextValue | null>(null);

function readStoredRegion() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = fixMaybeBrokenText(JSON.parse(raw)) as SiteRegion;
    return knownRegions.find((region) => region.code === parsed.code) ?? parsed;
  } catch {
    return null;
  }
}

export function RegionProvider({ children }: { children: ReactNode }) {
  const [region, setRegionState] = useState(defaultRegion);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const storedRegion = readStoredRegion();
    if (storedRegion) {
      setRegionState(storedRegion);
    }
    setIsReady(true);
  }, []);

  useEffect(() => {
    if (!isReady || typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(region));
  }, [isReady, region]);

  useEffect(() => {
    if (!isReady || typeof window === "undefined") {
      return;
    }

    const hasStoredRegion = Boolean(readStoredRegion());
    if (hasStoredRegion) {
      return;
    }

    let isActive = true;
    const resolveRegionByIp = async () => {
      try {
        const response = await fetch("https://ipwho.is/?lang=ru", {
          cache: "no-store"
        });
        const data = (await response.json()) as {
          success?: boolean;
          city?: string;
        };

        if (!isActive || !data.success || !data.city?.trim()) {
          return;
        }

        setRegionState(findRegionByCityName(data.city));
      } catch {
        // Keep the default region if geo-ip detection is unavailable.
      }
    };

    if (!("geolocation" in navigator)) {
      void resolveRegionByIp();
      return () => {
        isActive = false;
      };
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!isActive) {
          return;
        }

        setRegionState(
          findNearestRegion(position.coords.latitude, position.coords.longitude)
        );
      },
      () => {
        void resolveRegionByIp();
      },
      {
        enableHighAccuracy: false,
        timeout: 4000,
        maximumAge: 1000 * 60 * 60 * 6
      }
    );

    return () => {
      isActive = false;
    };
  }, [isReady]);

  const value = useMemo<RegionContextValue>(
    () => ({
      isReady,
      region,
      setRegion(nextRegion) {
        setRegionState(nextRegion);
      },
      setRegionByCityName(city) {
        setRegionState(findRegionByCityName(city));
      }
    }),
    [isReady, region]
  );

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
}

export function useRegion() {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error("useRegion must be used inside RegionProvider");
  }

  return context;
}
